import { Link, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { normalizeLang } from "@/i18n/languages";
import { categories } from "@/mocks/categories";

const MAIN_STORE = "https://kaspreadsheet.com";

export default function Footer() {
  const { t } = useTranslation();
  const location = useLocation();
  const segments = location.pathname.split("/").filter(Boolean);
  const lang = normalizeLang(segments[0]);

  const quickLinks = [
    { label: t("footer.link.mainStore"), url: MAIN_STORE },
    { label: t("footer.link.buyNow"), url: MAIN_STORE },
    { label: t("footer.link.completeGuide"), url: `/${lang}/articles/allchinabuy-spreadsheet-guide` },
    { label: t("footer.link.allCategories"), url: "#categories" },
    { label: t("footer.link.faq"), url: "#faq" },
  ];

  const popularCats = categories.slice(0, 5);

  return (
    <footer className="w-full bg-foreground-950">
      <div className="mx-auto w-full max-w-7xl px-4 py-14 md:px-6 md:py-16">
        <div className="grid grid-cols-1 gap-10 sm:grid-cols-2 lg:grid-cols-4">
          {/* Stay Updated */}
          <div className="lg:col-span-1">
            <h4 className="font-heading text-lg font-semibold text-white">
              {t("footer.stayUpdated")}
            </h4>
            <p className="mt-3 text-sm leading-relaxed text-foreground-300">
              {t("footer.tagline")}
            </p>
            <a
              href={MAIN_STORE}
              target="_blank"
              rel="noopener nofollow"
              className="mt-5 inline-flex items-center justify-center gap-2 rounded-full bg-white px-5 py-2.5 text-sm font-semibold text-foreground-950 transition-all hover:bg-white/90 whitespace-nowrap cursor-pointer"
            >
              {t("companion.cta.secondary")}
              <span className="w-4 h-4 flex items-center justify-center">
                <i className="ri-arrow-right-line text-sm"></i>
              </span>
            </a>
          </div>

          {/* Popular Categories */}
          <div>
            <h4 className="text-sm font-semibold uppercase tracking-wider text-white/60 mb-5">
              {t("footer.popularCategories")}
            </h4>
            <ul className="space-y-2.5">
              {popularCats.map((cat) => (
                <li key={cat.id}>
                  <a
                    href={cat.url}
                    target="_blank"
                    rel="noopener nofollow"
                    className="text-sm text-foreground-300 hover:text-white transition-colors cursor-pointer"
                  >
                    {cat.name[lang]}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="text-sm font-semibold uppercase tracking-wider text-white/60 mb-5">
              {t("footer.quickLinks")}
            </h4>
            <ul className="space-y-2.5">
              {quickLinks.map((link, i) => (
                <li key={i}>
                  {link.url.startsWith("http") ? (
                    <a
                      href={link.url}
                      target="_blank"
                      rel="noopener nofollow"
                      className="text-sm text-foreground-300 hover:text-white transition-colors cursor-pointer"
                    >
                      {link.label}
                    </a>
                  ) : (
                    <a
                      href={link.url}
                      className="text-sm text-foreground-300 hover:text-white transition-colors cursor-pointer"
                    >
                      {link.label}
                    </a>
                  )}
                </li>
              ))}
            </ul>
          </div>

          {/* About / Disclaimer */}
          <div>
            <h4 className="text-sm font-semibold uppercase tracking-wider text-white/60 mb-5">
              About
            </h4>
            <p className="text-sm leading-relaxed text-foreground-400">
              {t("footer.disclaimer")}
            </p>
          </div>
        </div>
      </div>

      {/* Bottom bar */}
      <div className="border-t border-white/10">
        <div className="mx-auto w-full max-w-7xl px-4 py-4 md:px-6">
          <div className="flex flex-col items-center gap-4 md:flex-row md:justify-between">
            <div className="flex items-center gap-2">
              <span className="w-7 h-7 flex items-center justify-center rounded-md bg-white/10 text-white">
                <i className="ri-table-line text-sm"></i>
              </span>
              <span className="font-body text-sm font-semibold text-white">
                AllChinaBuy Spreadsheet
              </span>
            </div>

            <div className="flex items-center gap-6">
              <Link
                to={`/${lang}`}
                className="text-sm text-foreground-300 hover:text-white transition-colors cursor-pointer"
              >
                {t("nav.home")}
              </Link>
              <a
                href="#guides"
                className="text-sm text-foreground-300 hover:text-white transition-colors cursor-pointer"
              >
                {t("nav.articles")}
              </a>
              <a
                href="#categories"
                className="text-sm text-foreground-300 hover:text-white transition-colors cursor-pointer"
              >
                {t("nav.categories")}
              </a>
              <a
                href="#faq"
                className="text-sm text-foreground-300 hover:text-white transition-colors cursor-pointer"
              >
                FAQ
              </a>
              <a
                href={MAIN_STORE}
                target="_blank"
                rel="noopener nofollow"
                className="inline-flex items-center gap-1.5 rounded-full bg-white px-4 py-2 text-xs font-semibold text-foreground-950 transition-all hover:bg-white/90 whitespace-nowrap cursor-pointer"
              >
                AllChinaBuy Sheet
                <span className="w-3.5 h-3.5 flex items-center justify-center">
                  <i className="ri-arrow-right-up-line text-xs"></i>
                </span>
              </a>
            </div>

            <p className="text-xs text-foreground-500">
              © {new Date().getFullYear()} {t("footer.rights")}
            </p>
          </div>
        </div>
      </div>
    </footer>
  );
}